import type { FilePublicWithPermissions } from '@repo/types/files';
import type { ReactNode } from 'react';

import { useDraggable } from '@dnd-kit/react';
import { useMemo } from 'react';

import { createFileDragData, useFsDragDropContext } from '@/components/features/files/dnd';
import { FS_DRAG_TYPE } from '@/hooks/fs/useFsDragDrop';
import { useFilesStorage } from '@/store/files-store';

interface DraggableFileProps {
  file: FilePublicWithPermissions;
  children: ReactNode;
}

/**
 * Wrapper component that makes a file draggable.
 * Only enables dragging if the user has edit permission on the file.
 * Shows isDragging opacity for all files in the dragged selection group.
 */
export function DraggableFile(draggableFileProps: DraggableFileProps) {
  const { file, children } = draggableFileProps;
  const canDrag = file.permissions.edit;

  const { isDragging: isContextDragging, draggedItem } = useFsDragDropContext();
  const { selected } = useFilesStorage();

  const { ref, isDragging: isThisItemDragging } = useDraggable({
    id: `file-${file.id}`,
    disabled: !canDrag,
    data: createFileDragData(file),
  });

  // Check if this file is part of the dragged selection group
  const isPartOfDraggedSelection = useMemo(() => {
    if (!isContextDragging || !draggedItem) return false;

    // Check if the dragged item is part of the selection
    const isDraggedItemSelected =
      draggedItem.type === FS_DRAG_TYPE.FILE ? selected.files.some((f) => f.id === draggedItem.entity.id) : selected.directories.some((d) => d.id === draggedItem.entity.id);

    // If dragged item is selected, check if this file is also in the selection
    if (isDraggedItemSelected) {
      return selected.files.some((f) => f.id === file.id);
    }

    return false;
  }, [isContextDragging, draggedItem, selected, file.id]);

  // Show dragging opacity if this item is being dragged OR is part of the dragged selection
  const showDraggingOpacity = isThisItemDragging || isPartOfDraggedSelection;

  return (
    <div
      ref={ref}
      style={{
        opacity: showDraggingOpacity ? 0.5 : 1,
        cursor: canDrag ? 'grab' : 'default',
      }}
    >
      {children}
    </div>
  );
}
